import { useState, useRef, useEffect } from "react";
import clsx from "clsx";

interface Props {
  id?: string;
  name?: string;
  length?: number;
  value?: string;
  onChange?: (value: string) => void;
  onComplete?: (value: string) => void;
  className?: string;
  disabled?: boolean;
  error?: boolean;
}

export default function VerificationCodeInput({
  id,
  name,
  length = 6,
  value = "",
  onChange,
  onComplete,
  className,
  disabled = false,
  error = false,
}: Props) {
  const [digits, setDigits] = useState<string[]>(
    value.split("").slice(0, length).concat(Array(length - value.length).fill(""))
  );
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    const newDigits = value.split("").slice(0, length).concat(Array(length - value.length).fill(""));
    setDigits(newDigits);
  }, [value, length]);

  const handleInputChange = (index: number, inputValue: string) => {
    // Only allow single digit
    const digit = inputValue.replace(/[^0-9]/g, "").slice(-1);
    
    const newDigits = [...digits];
    newDigits[index] = digit;
    setDigits(newDigits);

    const codeValue = newDigits.join("");
    onChange?.(codeValue);

    // Auto-focus next input
    if (digit && index < length - 1) {
      inputRefs.current[index + 1]?.focus();
    }

    // Check if code is complete
    if (codeValue.length === length && !codeValue.includes("")) {
      onComplete?.(codeValue);
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace") {
      if (!digits[index] && index > 0) {
        // If current input is empty, focus previous input
        inputRefs.current[index - 1]?.focus();
      } else {
        // Clear current input
        const newDigits = [...digits];
        newDigits[index] = "";
        setDigits(newDigits);
        onChange?.(newDigits.join(""));
      }
    } else if (e.key === "ArrowLeft" && index > 0) {
      inputRefs.current[index - 1]?.focus();
    } else if (e.key === "ArrowRight" && index < length - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").replace(/[^0-9]/g, "").slice(0, length);
    
    const newDigits = Array(length).fill("");
    for (let i = 0; i < pastedData.length; i++) {
      newDigits[i] = pastedData[i];
    }
    
    setDigits(newDigits);
    onChange?.(newDigits.join(""));
    
    // Focus the next empty input or the last input
    const nextIndex = Math.min(pastedData.length, length - 1);
    inputRefs.current[nextIndex]?.focus();
    
    if (pastedData.length === length) {
      onComplete?.(pastedData);
    }
  };

  return (
    <div className={clsx("flex gap-2 justify-center", className)}>
      {/* Hidden input for form submission */}
      <input
        type="hidden"
        id={id}
        name={name}
        value={digits.join("")}
      />
      
      {digits.map((digit, index) => (
        <input
          key={index}
          ref={(el) => {
            inputRefs.current[index] = el;
          }}
          type="text"
          inputMode="numeric"
          maxLength={1}
          value={digit}
          onChange={(e) => handleInputChange(index, e.target.value)}
          onKeyDown={(e) => handleKeyDown(index, e)}
          onPaste={handlePaste}
          disabled={disabled}
          className={clsx(
            "w-12 h-12 text-center text-xl font-semibold border-2 rounded-lg",
            "focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500",
            "transition-all duration-200",
            {
              "border-gray-300 bg-white": !error && !disabled,
              "border-red-500 bg-red-50": error,
              "border-gray-200 bg-gray-100 cursor-not-allowed": disabled,
              "border-purple-500 bg-purple-50": digit && !error && !disabled,
            }
          )}
          aria-label={`Digit ${index + 1}`}
        />
      ))}
    </div>
  );
}
